// FRONT-1698: the composer's model control (flag `eddy.model-picker-composer`)
// — a model button plus the thinking slider, replacing the Fast/Thoughtful
// pills. `sessionId` is the conversation the composer belongs to; without one
// (the pre-session launcher composer) the control reads/writes only the
// person's standing pick, which seeds the next new chat.

import { useId, useState } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { ThinkingSlider } from '~/components/thinking-slider';
import {
  useChatModel,
  useModelPickerPrefs,
  usePickChatModel,
  useSetDefaultChatModel,
  useSetThinkingLevel,
  useStandingChatModel,
  useThinkingLevel,
  useUpdateModelPickerPrefs,
} from '~/hooks/queries/use-model-picker';
import {
  activeModelReasons,
  activeModelTopTiers,
  modelButtonLabel,
  modelControlReason,
  resolveActiveModel,
  shortModelName,
  thinkingControlReason,
  toggleStar,
} from './model-picker-logic';
import { ModelPickerDialog, ModelPickerPanel } from './model-picker-dialog';
import { HubModelMenu, type ModelPanel } from './hub-model-menu';

const PILL =
  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 disabled:opacity-50';
const PILL_IDLE = 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50';

/** Opens the window only once prefs exist; before that the click is dropped, not queued. */
export function openWindowIfReady(prefs: unknown, open: () => void): boolean {
  if (!prefs) return false;
  open();
  return true;
}

export function ComposerModelPicker({
  sessionId,
  disabled,
  panel = 'closed',
  onPanelChange,
  panelSlot = null,
  saveSlot = null,
  selectionSlot = null,
}: {
  sessionId?: string;
  disabled?: boolean;
  /** A Hub workspace's model panel opens inside the composer box: its state, setter and the slot it renders into. */
  panel?: ModelPanel;
  onPanelChange?: (panel: ModelPanel) => void;
  panelSlot?: HTMLElement | null;
  /** Where the chooser's Save button stands (Send's place in the composer's button row). */
  saveSlot?: HTMLElement | null;
  /** Where the chooser's current-selection header stands (the composer's footer, above the buttons). */
  selectionSlot?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  const prefsQuery = useModelPickerPrefs();
  const sessionQuery = useChatModel(sessionId);
  const standingQuery = useStandingChatModel(!sessionId);
  const pick = usePickChatModel(sessionId);
  const updatePrefs = useUpdateModelPickerPrefs();
  const thinkingQuery = useThinkingLevel(sessionId);
  const setThinking = useSetThinkingLevel(sessionId);
  const setDefault = useSetDefaultChatModel();

  const prefs = prefsQuery.data;
  const active = resolveActiveModel({
    sessionId,
    sessionState: sessionQuery.data,
    standingModel: standingQuery.data?.model,
    prefs,
  });
  const shortName = active.name ? shortModelName(active.name) : null;
  // The window only exists once prefs have loaded; before that a click is
  // ignored, never remembered — otherwise it would pop open by surprise later.
  const reason = modelControlReason({
    busy: !!disabled,
    prefsLoaded: !!prefs,
    prefsFailed: prefsQuery.isError,
  });
  const reasonId = useId();
  const openWindow = () => openWindowIfReady(prefs, () => setOpen(true));
  const thinkingReason = thinkingControlReason({
    busy: !!disabled,
    loaded: !!thinkingQuery.data,
    failed: thinkingQuery.isError,
    modelReasons: activeModelReasons(prefs, active.id),
  });

  // Until the model list has answered, nobody knows which selector this workspace has: a neutral, disabled
  // button that says why — never the old pill and slider, which would flash first and then be swapped away.
  if (prefsQuery.isPending) {
    return (
      <div className="flex items-center">
        <button
          type="button"
          data-testid="composer-model-button"
          aria-busy="true"
          aria-describedby={reason ? reasonId : undefined}
          aria-label={modelButtonLabel(null)}
          disabled
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground opacity-50"
        >
          <span aria-hidden className="h-3 w-24 animate-pulse rounded bg-muted" />
          <CaretDownIcon size={11} weight="bold" className="shrink-0" />
        </button>
        {reason && (
          <span id={reasonId} className="sr-only" data-testid="composer-model-reason">
            {reason}
          </span>
        )}
      </div>
    );
  }

  const dialog = prefs && (
    <ModelPickerDialog
      open={open}
      onOpenChange={setOpen}
      prefs={prefs}
      pickId={active.pickId}
      onPick={(id) => pick.mutate(id)}
      onReorder={(order) => updatePrefs.mutate({ order })}
      onToggleStar={(id) => updatePrefs.mutate({ starred: toggleStar(prefs.starred ?? [], id) })}
      onSetDefault={(id) => setDefault.mutate(id)}
    />
  );

  // FRONT-3249 (behind eddy.model-picker-redesign): one dense menu — model, cloud and thinking — instead of a button and a slider.
  // It opens inside the composer box (no popover, no dialog): the box shows the menu, or the full chooser for "All models".
  if (prefs?.listSource === 'hub' && onPanelChange) {
    return (
      <div className="flex min-w-0 items-center">
        <HubModelMenu
          prefs={prefs}
          activeId={active.id}
          thinking={thinkingQuery.data?.level}
          topTiers={activeModelTopTiers(prefs, active.id)}
          disabledReason={reason}
          onPick={(id) => pick.mutate(id)}
          onThinking={(level) => setThinking.mutate(level)}
          panel={panel}
          onPanelChange={onPanelChange}
          panelSlot={panelSlot}
          allModels={
            <ModelPickerPanel
              prefs={prefs}
              pickId={active.pickId}
              onPick={(id) => pick.mutate(id)}
              // Save: apply the selected model (above), then give the box its text line back.
              onDone={() => onPanelChange('closed')}
              saveSlot={saveSlot}
              selectionSlot={selectionSlot}
              onReorder={(order) => updatePrefs.mutate({ order })}
              onToggleStar={(id) => updatePrefs.mutate({ starred: toggleStar(prefs.starred ?? [], id) })}
              onSetDefault={(id) => setDefault.mutate(id)}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="ghost"
        size="xs"
        tooltip={reason ?? active.name ?? 'Choose a model'}
        aria-describedby={reason ? reasonId : undefined}
        data-testid="composer-model-button"
        aria-label={modelButtonLabel(shortName)}
        data-model-id={active.id ?? undefined}
        onClick={openWindow}
        disabled={reason !== null}
        // Fixed width, so the slider beside it never moves when the name changes.
        className={cn(PILL, PILL_IDLE, 'w-[9.5rem] justify-between')}
      >
        <span className="truncate">{shortName ?? 'Model'}</span>
        <CaretDownIcon size={11} weight="bold" className="shrink-0" />
      </Button>
      <ThinkingSlider
        value={thinkingQuery.data?.level}
        onChange={(level) => setThinking.mutate(level)}
        disabledReason={thinkingReason}
        cappedAtHigh={!activeModelTopTiers(prefs, active.id)}
        ariaLabel="Thinking level"
        testId="composer-thinking-slider"
      />
      {reason && (
        <span id={reasonId} className="sr-only" data-testid="composer-model-reason">
          {reason}
        </span>
      )}
      {dialog}
    </div>
  );
}
